import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  Plus,
  Trash2,
  Star,
  Upload,
  RefreshCw,
  X,
  Check,
  Loader2,
  AlertTriangle,
  Pencil,
} from 'lucide-react';

import { Section } from '@/components/primitives/Section';
import { EmptyState } from '@/components/primitives/EmptyState';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { geometryToPolygons, boundaryStyle } from '@/utils/boundaryGeometry';

// Legacy JS api modules — typed via legacy-shims.d.ts.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
import * as boundariesApi from '@/api/boundaries';
// eslint-disable-next-line @typescript-eslint/no-explicit-any
import * as parcelsApi from '@/api/parcels';
// eslint-disable-next-line @typescript-eslint/no-explicit-any
import { triggerReindex, getReindexRun } from '@/api/parcelPipeline';

import { Polygon } from 'react-leaflet';
import { MapCanvas } from '@/components/maps/MapCanvas';
import type { DrawnEvent } from '@/components/maps/MapDrawControl';

import type { FPATenant } from '../FPASettingsHub';

// ── Types & helpers ────────────────────────────────────────────────── //

interface BoundaryRecord {
  id: number;
  tenant_id?: number;
  name: string;
  boundary_name?: string;
  description?: string | null;
  is_primary?: boolean;
  area_hectares?: number | null;
  geometry?: { type: string; coordinates: unknown } | null;
}

// Boundary geometry conversion + styling now live in
// `@/utils/boundaryGeometry` (shared with BoundariesOverlay).

// Polygon-only draw options. We restrict to polygons to keep the editor
// focused — circles/rectangles round-trip through GeoJSON as polygons
// anyway, and admins find a single tool less confusing than five.
const POLYGON_ONLY_DRAW = Object.freeze({
  rectangle: false,
  circle: false,
  circlemarker: false,
  marker: false,
  polyline: false,
  polygon: {
    allowIntersection: false,
    showArea: true,
    shapeOptions: {
      color: '#1976d2',
      fillColor: '#42a5f5',
      fillOpacity: 0.3,
      weight: 2,
    },
  },
});

// Edit-mode draw options: hide every "create" tool so only the Edit /
// Delete buttons in the toolbar remain. The user is here to re-shape an
// existing polygon, not to add a new one.
const EDIT_ONLY_DRAW = Object.freeze({
  rectangle: false,
  circle: false,
  circlemarker: false,
  marker: false,
  polyline: false,
  polygon: false,
});

// ── Component ──────────────────────────────────────────────────────── //

interface BoundariesTabProps {
  tenantId: number;
  tenant: FPATenant | null;
}

type Mode = 'view' | 'drawing' | 'editing';

export default function BoundariesTab({ tenantId, tenant }: BoundariesTabProps) {
  const [boundaries, setBoundaries] = useState<BoundaryRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Spatial-editor state machine.
  const [mode, setMode] = useState<Mode>('view');
  const [pendingGeometry, setPendingGeometry] = useState<
    { type: string; coordinates: unknown } | null
  >(null);
  const [previewCount, setPreviewCount] = useState<number | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const previewSeqRef = useRef(0);

  // When mode === 'editing', which saved boundary is being re-shaped.
  const [editingId, setEditingId] = useState<number | null>(null);

  // Naming dialog (shown after the admin commits a drawn shape).
  const [nameDialogOpen, setNameDialogOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [saving, setSaving] = useState(false);

  // Reindex orchestration.
  const [reindexing, setReindexing] = useState(false);

  // Per-row in-flight flags.
  const [busyId, setBusyId] = useState<number | null>(null);

  // Map center prefers the tenant's lat/lng; falls back to a sensible
  // South African default — both legacy pages used the same coords.
  const center: [number, number] =
    tenant?.latitude != null && tenant?.longitude != null
      ? [Number(tenant.latitude), Number(tenant.longitude)]
      : [-25.6429, 28.3433];

  // ── Data load ─────────────────────────────────────────────────────── //

  const refresh = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const data = await boundariesApi.listBoundaries({
        tenant_id: tenantId,
        page_size: 200,
      });
      setBoundaries(data?.boundaries ?? []);
    } catch (err) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const e = err as any;
      setLoadError(e?.response?.data?.detail || e?.message || 'Load failed');
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // ── Drawing flow ──────────────────────────────────────────────────── //

  const enterDrawMode = () => {
    setPendingGeometry(null);
    setPreviewCount(null);
    setEditingId(null);
    setMode('drawing');
  };

  const exitDrawMode = () => {
    setPendingGeometry(null);
    setPreviewCount(null);
    setEditingId(null);
    setMode('view');
  };

  // ── Editing flow (re-shape a saved boundary) ─────────────────────── //

  const enterEditMode = (b: BoundaryRecord) => {
    if (!b.geometry) {
      toast.error('That boundary has no geometry to edit.');
      return;
    }
    setEditingId(b.id);
    // Seed pendingGeometry with the current shape so "Save" without any
    // drag is a no-op (rather than a 400 because pendingGeometry is null).
    setPendingGeometry(b.geometry);
    setPreviewCount(null);
    setMode('editing');
    runPreview(b.geometry);
  };

  const exitEditMode = () => {
    setEditingId(null);
    setPendingGeometry(null);
    setPreviewCount(null);
    setMode('view');
  };

  const commitEdit = async () => {
    if (editingId == null || !pendingGeometry) return;
    setSaving(true);
    try {
      await boundariesApi.updateBoundary(
        editingId,
        { geometry: pendingGeometry },
        { tenant_id: tenantId },
      );
      toast.success('Boundary updated');
      exitEditMode();
      await refresh();
    } catch (err) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const e = err as any;
      toast.error(e?.response?.data?.detail || e?.message || 'Update failed');
    } finally {
      setSaving(false);
    }
  };

  const runPreview = useCallback(
    async (geometry: { type: string; coordinates: unknown }) => {
      // Sequence guard: an older request finishing after a newer one
      // would overwrite the more-recent count with a stale value.
      const seq = ++previewSeqRef.current;
      setPreviewing(true);
      try {
        const result = await parcelsApi.previewParcelCount(geometry);
        if (seq !== previewSeqRef.current) return;
        setPreviewCount(typeof result?.count === 'number' ? result.count : 0);
      } catch (err) {
        if (seq !== previewSeqRef.current) return;
        console.warn('Parcel preview failed:', err);
        setPreviewCount(null);
      } finally {
        if (seq === previewSeqRef.current) setPreviewing(false);
      }
    },
    [],
  );

  // MapCanvas's drawing.onCreated / onEdited hands us the GeoJSON geometry
  // directly via the DrawnEvent, so we don't have to dig into layer refs.
  const handleDrawCreated = useCallback(
    (e: DrawnEvent) => {
      if (!e.geometry) return;
      setPendingGeometry(e.geometry);
      runPreview(e.geometry);
    },
    [runPreview],
  );

  const handleDrawEdited = useCallback(
    (e: DrawnEvent) => {
      if (!e.geometry) return;
      setPendingGeometry(e.geometry);
      runPreview(e.geometry);
    },
    [runPreview],
  );

  // ── Save / delete / set-primary ───────────────────────────────────── //

  const commitNew = async () => {
    if (!pendingGeometry || !newName.trim()) return;
    setSaving(true);
    try {
      await boundariesApi.createBoundary({
        tenant_id: tenantId,
        name: newName.trim(),
        geometry: pendingGeometry,
      });
      toast.success(`Boundary "${newName.trim()}" created`);
      setNameDialogOpen(false);
      setNewName('');
      exitDrawMode();
      await refresh();
    } catch (err) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const e = err as any;
      toast.error(e?.response?.data?.detail || e?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (b: BoundaryRecord) => {
    const warn = b.is_primary
      ? `"${b.name}" is the primary boundary used by parcel reindex. Delete anyway?`
      : `Delete boundary "${b.name}"?`;
    if (!window.confirm(warn)) return;
    setBusyId(b.id);
    try {
      await boundariesApi.deleteBoundary(b.id, { tenant_id: tenantId });
      toast.success(`Boundary "${b.name}" deleted`);
      await refresh();
    } catch (err) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const e = err as any;
      toast.error(e?.response?.data?.detail || e?.message || 'Delete failed');
    } finally {
      setBusyId(null);
    }
  };

  const handleSetPrimary = async (b: BoundaryRecord) => {
    if (b.is_primary) return;
    setBusyId(b.id);
    try {
      await boundariesApi.setPrimary(b.id, { tenant_id: tenantId });
      toast.success(`"${b.name}" is now the primary boundary`);
      await refresh();
    } catch (err) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const e = err as any;
      toast.error(
        e?.response?.data?.detail || e?.message || 'Set-primary failed',
      );
    } finally {
      setBusyId(null);
    }
  };

  // ── GeoJSON upload (legacy escape hatch) ──────────────────────────── //

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [uploading, setUploading] = useState(false);
  const UPLOAD_SIZE_THRESHOLD = 6 * 1024 * 1024;

  const handleUploadGeoJSON = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setUploading(true);
    try {
      let result: { imported?: number; updated?: number; skipped?: number };
      if (file.size > UPLOAD_SIZE_THRESHOLD) {
        const presign = await boundariesApi.getUploadUrl({
          filename: file.name,
          content_type: file.type || 'application/geo+json',
        });
        await fetch(presign.upload_url, {
          method: 'PUT',
          body: file,
          headers: { 'Content-Type': file.type || 'application/geo+json' },
        });
        result = await boundariesApi.importFromS3({
          tenant_id: tenantId,
          s3_key: presign.s3_key,
        });
      } else {
        const formData = new FormData();
        formData.append('file', file);
        result = await boundariesApi.importFromFile(tenantId, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }
      const parts = [`Imported: ${result.imported ?? 0}`];
      if (result.updated) parts.push(`Updated: ${result.updated}`);
      if (result.skipped) parts.push(`Skipped: ${result.skipped}`);
      toast.success(parts.join(' · '));
      await refresh();
    } catch (err) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const e = err as any;
      toast.error(e?.response?.data?.detail || e?.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  // ── Reindex orchestration ─────────────────────────────────────────── //

  const handleReindex = async () => {
    // The legacy /tenants/{id}/repartition endpoint scans the *DynamoDB*
    // parcel table and counts overlap with this tenant's boundary. Since
    // the parcel pipeline moved to S3 Parquet, that scan returns 0 rows
    // → "parcels_checked": 0. The working path is the chunked
    // spatial-join the admin Parcel Pipeline uses; we trigger the same
    // job here and poll its status.
    if (
      !window.confirm(
        'Run parcel reindex now? This spatial-joins every source parquet ' +
          "chunk against every FPA's boundaries (including this one) and " +
          'writes per-FPA parcel shards to S3. Takes ~10–20 minutes ' +
          'depending on chunk count. Continue?',
      )
    ) {
      return;
    }
    setReindexing(true);
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const queued = (await triggerReindex()) as any;
      const parentJobId = queued?.parent_job_id;
      if (parentJobId == null) {
        throw new Error('Backend did not return a parent_job_id');
      }
      toast.success(
        'Parcel reindex queued — running in the background. ' +
          'You can also monitor progress under Admin → Parcel Pipeline.',
      );

      // Light polling — same 20-min ceiling, but tracks the new run-status
      // shape (`status: queued | processing | completed | failed`).
      const start = Date.now();
      const POLL_MS = 5000;
      const MAX_MS = 25 * 60 * 1000;
      while (Date.now() - start < MAX_MS) {
        await new Promise((r) => setTimeout(r, POLL_MS));
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const run = (await getReindexRun(parentJobId)) as any;
        if (run?.status === 'completed') {
          toast.success(
            `Parcels reindexed (${run?.total_matched_parcels?.toLocaleString?.() ?? '?'} matched).`,
          );
          return;
        }
        if (run?.status === 'failed') {
          toast.error(run?.error || 'Reindex failed');
          return;
        }
      }
      toast.error(
        'Reindex still running after 25 minutes — check Admin → Parcel Pipeline for status.',
      );
    } catch (err) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const e = err as any;
      toast.error(
        e?.response?.data?.detail || e?.message || 'Reindex failed to queue',
      );
    } finally {
      setReindexing(false);
    }
  };

  // ── Map polygons ──────────────────────────────────────────────────── //

  const polygonsForMap = useMemo(() => {
    // Existing boundaries are dimmed while the user is drawing so the new
    // shape stands out; otherwise primary gets warm-coloured and the rest
    // are a calmer purple. When editing one boundary, exclude it from the
    // static layer (it's seeded as an editable layer instead).
    const style = (isPrimary: boolean) =>
      boundaryStyle(isPrimary, { fillOpacity: mode === 'view' ? 0.1 : 0.04 });
    return boundaries
      .filter((b) => b.id !== editingId)
      .flatMap((b) =>
        geometryToPolygons(b.geometry, b.id).map((poly) => ({
          positions: poly.positions,
          ...style(Boolean(b.is_primary)),
        })),
      );
  }, [boundaries, mode, editingId]);

  // Geometry to seed into the editable FeatureGroup when in edit mode.
  const editableGeometry = useMemo(() => {
    if (mode !== 'editing' || editingId == null) return null;
    return boundaries.find((b) => b.id === editingId)?.geometry ?? null;
  }, [mode, editingId, boundaries]);

  // ── Render ────────────────────────────────────────────────────────── //

  const primary = boundaries.find((b) => b.is_primary) || null;

  return (
    <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_2fr]">
      {/* ── Left: list + actions ───────────────────────────────────── */}
      <div className="space-y-4">
        <Section
          title="Boundaries"
          description={
            boundaries.length
              ? `${boundaries.length} defined`
              : 'No boundaries yet — draw one on the map.'
          }
          actions={
            mode === 'view' ? (
              <Button
                size="sm"
                onClick={enterDrawMode}
                disabled={loading}
                className="gap-1"
              >
                <Plus className="h-4 w-4" />
                Add
              </Button>
            ) : mode === 'editing' ? (
              <Button size="sm" variant="outline" onClick={exitEditMode}>
                Cancel edit
              </Button>
            ) : (
              <Button size="sm" variant="outline" onClick={exitDrawMode}>
                Cancel drawing
              </Button>
            )
          }
        >
          {loadError && (
            <Alert variant="destructive" className="mb-3">
              <AlertDescription>{loadError}</AlertDescription>
            </Alert>
          )}

          {loading ? (
            <div className="space-y-2">
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
            </div>
          ) : boundaries.length === 0 ? (
            <EmptyState
              title="No boundaries yet"
              description="Boundaries are the FPA's footprint. Draw one on the map, or upload an existing GeoJSON."
            />
          ) : (
            <ul className="space-y-2">
              {boundaries.map((b) => {
                const area =
                  typeof b.area_hectares === 'number'
                    ? `${b.area_hectares.toLocaleString(undefined, { maximumFractionDigits: 1 })} ha`
                    : null;
                return (
                  <li
                    key={b.id}
                    className={cn(
                      'flex items-start justify-between gap-3 rounded-md border p-3',
                      b.is_primary && 'border-primary/40 bg-primary/5',
                    )}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="truncate font-medium">{b.name}</span>
                        {b.is_primary && (
                          <Badge variant="default" className="gap-1">
                            <Star className="h-3 w-3" />
                            Primary
                          </Badge>
                        )}
                      </div>
                      {b.description && (
                        <p className="mt-1 text-xs text-muted-foreground line-clamp-2">
                          {b.description}
                        </p>
                      )}
                      <div className="mt-1 flex flex-wrap gap-2 text-xs text-muted-foreground">
                        {area && <span>{area}</span>}
                        {b.boundary_name && (
                          <code className="rounded bg-muted px-1.5 py-0.5">
                            {b.boundary_name}
                          </code>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-shrink-0 items-center gap-1">
                      {!b.is_primary && (
                        <Button
                          size="icon"
                          variant="ghost"
                          title="Make primary"
                          disabled={busyId === b.id || mode !== 'view'}
                          onClick={() => handleSetPrimary(b)}
                        >
                          <Star className="h-4 w-4" />
                        </Button>
                      )}
                      <Button
                        size="icon"
                        variant="ghost"
                        title="Edit shape"
                        disabled={busyId === b.id || mode !== 'view'}
                        onClick={() => enterEditMode(b)}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        title="Delete"
                        disabled={busyId === b.id || mode !== 'view'}
                        onClick={() => handleDelete(b)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}

          {/* Secondary actions: file upload + reindex. */}
          <div className="mt-4 flex flex-wrap gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={uploading}
              onClick={() => fileInputRef.current?.click()}
              className="gap-1"
            >
              <Upload className="h-4 w-4" />
              {uploading ? 'Uploading…' : 'Upload GeoJSON'}
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              hidden
              accept=".geojson,.json,application/geo+json,application/json"
              onChange={handleUploadGeoJSON}
            />
            <Button
              variant="outline"
              size="sm"
              disabled={reindexing || !primary}
              onClick={handleReindex}
              className="gap-1"
              title={
                primary
                  ? 'Re-assign parcels to this FPA using the primary boundary'
                  : 'Set a primary boundary first'
              }
            >
              {reindexing ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4" />
              )}
              Reindex parcels
            </Button>
          </div>

          {!primary && boundaries.length > 0 && (
            <Alert className="mt-3">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                No primary boundary set. Pick one with the star icon — reindex
                won&apos;t run without it.
              </AlertDescription>
            </Alert>
          )}
        </Section>
      </div>

      {/* ── Right: map ─────────────────────────────────────────────── */}
      <Section
        unpadded
        title={
          mode === 'drawing'
            ? 'Draw a new boundary'
            : mode === 'editing'
              ? 'Edit boundary'
              : 'Map'
        }
        description={
          mode === 'drawing'
            ? 'Use the polygon tool (top-left). Close the shape to see the parcel preview.'
            : mode === 'editing'
              ? 'Click the pencil tool, drag the vertex handles, then click Save changes.'
              : 'Existing boundaries shown — primary is highlighted.'
        }
      >
        <div className="relative h-[60vh] min-h-[420px] overflow-hidden rounded-b-lg">
          <MapCanvas
            center={center}
            zoom={polygonsForMap.length || editableGeometry ? 10 : 9}
            variant="lg"
            tenantId={tenantId}
            mapSearch
            className="!h-full !min-h-0 !rounded-none"
            drawing={
              mode === 'drawing' || mode === 'editing'
                ? {
                    enabled: true,
                    position: 'topleft',
                    initialGeometry: editableGeometry as
                      | GeoJSON.Polygon
                      | GeoJSON.MultiPolygon
                      | null,
                    drawOptions:
                      mode === 'editing' ? EDIT_ONLY_DRAW : POLYGON_ONLY_DRAW,
                    onCreated: handleDrawCreated,
                    onEdited: handleDrawEdited,
                  }
                : undefined
            }
            fitBoundsTo={
              polygonsForMap.length > 0 || editableGeometry
                ? { polygons: polygonsForMap }
                : false
            }
          >
            {polygonsForMap.map((p, idx) => (
              <Polygon
                key={`boundary-${idx}`}
                positions={p.positions}
                pathOptions={{
                  color: p.color,
                  fillColor: p.fillColor,
                  fillOpacity: p.fillOpacity,
                  weight: p.weight,
                  dashArray: p.dashArray,
                }}
              />
            ))}
          </MapCanvas>

          {/* Edit banner — floats over the map while editing a saved
              boundary. Save commits the edited shape via PATCH. */}
          {mode === 'editing' && (
            <div className="pointer-events-none absolute inset-x-0 bottom-3 z-[1000] flex justify-center">
              <div className="pointer-events-auto flex items-center gap-3 rounded-full border bg-background/95 px-4 py-2 shadow-lg backdrop-blur">
                {previewing ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">
                      Counting parcels…
                    </span>
                  </>
                ) : (
                  <Badge variant="secondary" className="text-sm">
                    {previewCount == null
                      ? '—'
                      : previewCount.toLocaleString()}{' '}
                    parcels inside
                  </Badge>
                )}
                <Button
                  size="sm"
                  variant="ghost"
                  className="gap-1"
                  onClick={exitEditMode}
                  disabled={saving}
                >
                  <X className="h-4 w-4" />
                  Cancel
                </Button>
                <Button
                  size="sm"
                  className="gap-1"
                  onClick={commitEdit}
                  disabled={saving || !pendingGeometry}
                >
                  {saving ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Check className="h-4 w-4" />
                  )}
                  Save changes
                </Button>
              </div>
            </div>
          )}

          {/* Live-preview banner — floats over the map while drawing. */}
          {mode === 'drawing' && (
            <div className="pointer-events-none absolute inset-x-0 bottom-3 z-[1000] flex justify-center">
              <div className="pointer-events-auto flex items-center gap-3 rounded-full border bg-background/95 px-4 py-2 shadow-lg backdrop-blur">
                {!pendingGeometry ? (
                  <span className="text-sm text-muted-foreground">
                    Draw a polygon to see how many parcels it covers
                  </span>
                ) : previewing ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">
                      Counting parcels…
                    </span>
                  </>
                ) : (
                  <>
                    <Badge variant="secondary" className="text-sm">
                      {previewCount == null
                        ? '—'
                        : previewCount.toLocaleString()}{' '}
                      parcels inside
                    </Badge>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="gap-1"
                      onClick={exitDrawMode}
                    >
                      <X className="h-4 w-4" />
                      Discard
                    </Button>
                    <Button
                      size="sm"
                      className="gap-1"
                      onClick={() => {
                        setNewName('');
                        setNameDialogOpen(true);
                      }}
                    >
                      <Check className="h-4 w-4" />
                      Save boundary
                    </Button>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </Section>

      {/* ── Name dialog (post-draw) ────────────────────────────────── */}
      <Dialog open={nameDialogOpen} onOpenChange={setNameDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Name this boundary</DialogTitle>
            <DialogDescription>
              {previewCount != null && (
                <>
                  The drawn area contains <strong>{previewCount.toLocaleString()}</strong>{' '}
                  parcels.
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <Input
            value={newName}
            autoFocus
            placeholder="e.g. Northern sector"
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && newName.trim() && !saving) commitNew();
            }}
          />
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setNameDialogOpen(false)}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button onClick={commitNew} disabled={!newName.trim() || saving}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
